<script>
import {
  CdrImg, CdrLink, CdrText,
} from '@rei/cedar';
import SimpleTopCategories from '@rei/simple-top-categories';

export default {
  name: 'QuadGridCategories',
  components: {
    SimpleTopCategories,
    CdrImg,
    CdrLink,
    CdrText,
  },
  props: {
    heading: {
      type: Object,
      default: () => {},
    },
    subheading: {
      type: String,
      default: '',
    },
    categories: {
      type: Array,
      default: () => [],
    },
    featuredCategory: {
      type: Object,
      default: () => {},
    },
    categoriesAlignment: { type: String, default: 'left' },
    reverse: { type: Boolean, default: false },
  },
  data() {
    return {
      featuredCategoryHovered: false,
      tileHeight: '0',
    };
  },
  computed: {
    featuredHeadingModifier() {
      return this.featuredCategory?.sale ? 'quad-grid__featured-heading--sale' : '';
    },
    reverseModifier() {
      return this.reverse
        ? 'quad-grid--reverse'
        : '';
    },
    categoriesAlignmentModifier() {
      return this.categoriesAlignment === 'center' ? 'quad-grid__categories-list--center' : '';
    },
    cssVars() {
      return {
        '--tile-height': this.tileHeight,
      };
    },
  },
  mounted() {
    this.getTileHeight();
    if (window && window?.addEventListener) {
      window.addEventListener('resize', this.getTileHeight);
    }
  },
  unmounted() {
    if (window && window?.removeEventListener) {
      window?.removeEventListener('resize', this.getTileHeight);
    }
  },
  methods: {
    getTileHeight() {
      const categoryListContainer = this.$refs?.tiles?.$el?.children[0];
      const bottomImageTitleLeft = categoryListContainer?.children[2]?.children[0]?.clientHeight;
      const bottomImageTitleRight = categoryListContainer?.children[3]?.children[0]?.clientHeight;
      const spacingDiff = bottomImageTitleLeft > bottomImageTitleRight
        ? bottomImageTitleLeft
        : bottomImageTitleRight;
      this.tileHeight = `${this.$refs?.tiles?.$el?.clientHeight - spacingDiff}px`;
    },
  },
};
</script>

<template>
  <div class="quad-grid-categories">
    <cdr-text
      v-if="heading"
      tag="h2"
      class="quad-grid__heading"
    >
      {{ heading?.text }}
    </cdr-text>
    <cdr-text
      v-if="subheading"
      tag="p"
      class="quad-grid__subheading"
    >
      {{ subheading }}
    </cdr-text>
    <div
      :style="cssVars"
      :class="['quad-grid', reverseModifier]"
    >
      <div class="quad-grid__featured-main">
        <div
          ref="featuredCategoryMedia"
          class="quad-grid__featured-media"
        >
          <cdr-img
            :src="featuredCategory?.media?.xs"
            :alt="featuredCategory?.media?.alt"
            class="quad-grid__featured-image"
            ratio="square"
            loading="lazy"
            fit="cover"
          />
        </div>
        <div class="quad-grid__featured-content">
          <cdr-text
            v-if="featuredCategory?.heading?.text"
            tag="h2"
            :class="['quad-grid__featured-heading', featuredHeadingModifier]"
          >
            {{ featuredCategory.heading.text }}
          </cdr-text>
          <cdr-link
            v-if="featuredCategory?.cta?.target"
            :href="featuredCategory?.cta?.target"
            :data-analytics-id="`${featuredCategory?.cta?.text} image_1`"
            modifier="standalone"
            class="quad-grid__featured-link"
            @mouseenter="featuredCategoryHovered = true"
            @mouseleave="featuredCategoryHovered = false"
          >
            {{ featuredCategory?.cta?.text }}
          </cdr-link>
          <p
            v-if="featuredCategory.body"
            class="quad-grid__featured-body"
            v-html="featuredCategory.body"
          />
        </div>
      </div>
      <div
        ref="quadGridCategoriesList"
        :class="['quad-grid__categories-list',categoriesAlignmentModifier]"
      >
        <simple-top-categories
          ref="tiles"
          :categories="categories"
          :media-config="{
            ratio: '1/1',
            hasBackground: true,
            fit: 'contain',
            padding: {
              mobile: '1.2rem',
              desktop: '1.6rem',
            },
          }"
          :image-ratio="{
            xs: '100%',
            sm: '100%',
            md: '100%',
            lg: '100%',
          }"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.quad-grid {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto auto;
  position: relative;
  grid-row-gap: $cdr-space-three-quarter-x;

  @include cdr-sm-mq-up {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    grid-column-gap: $cdr-space-two-x;

    &#{&}--reverse {
      .quad-grid__featured-main,
      .quad-grid__featured-content {
        grid-column: 2;
      }
      .quad-grid__categories-list {
        grid-column: 1;
        grid-row: 1;
      }
    }
  }

  @include cdr-md-mq-up {
    grid-column-gap: $cdr-space-three-x;
  }

  @include cdr-lg-mq-up {
    grid-column-gap: $cdr-space-four-x;
  }

  &__featured {
    &-main {
      position: relative;
      height: fit-content;
      grid-column: 1;
      grid-row: 1;
      display: flex;
      flex-direction: column;
    }

    &-content {
      grid-column: 1;
      grid-row: 2
    }

    &-body {
      @include cdr-text-body-300;
      margin: 0 0 $cdr-space-quarter-x;
    }

    &-media {
      overflow: hidden;
      flex: 1;
      margin-bottom: $cdr-space-half-x;

      @include cdr-md-mq-up {
        margin-bottom: $cdr-space-three-quarter-x;
      }
    }

    &-image {
      display: block;
      border-radius: 8px;
      @include cdr-sm-mq-up {
        height: calc(var(--tile-height) - $cdr-space-half-x);
      }

      @include cdr-md-mq-up {
        height: calc(var(--tile-height) - $cdr-space-three-quarter-x);
        border: none;
      }

    }

    &-heading {
      @include cdr-text-heading-sans-500;
      margin-bottom: $cdr-space-quarter-x;
      @include cdr-sm-mq-up {
        @include cdr-text-heading-sans-600;
        margin-bottom: $cdr-space-half-x;
      }

      &--sale {
        color: $cdr-color-text-sale;
      }
    }

    &-link {
      @include cdr-text-heading-sans-300;
      @include cdr-sm-mq-up {
        @include cdr-text-heading-sans-400;
      }
      &,
      &:hover,
      &:visited,
      &:active {
        color: $cdr-color-text-primary;
      }

      &::after {
        content: "";
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

  &__categories-list {
    grid-column: 1;
    grid-row: 3;

    @include cdr-sm-mq-up {
      grid-column: 2;
      grid-row: 1;
    }

    :deep(.simple-top-categories__grid) {
      grid-template-columns: repeat(2 ,1fr);
      row-gap: $cdr-space-one-x;
      column-gap: $cdr-space-three-quarter-x;

      @include cdr-sm-mq-up {
        column-gap: $cdr-space-one-x;
      }

      @include cdr-md-mq-up {
        row-gap: $cdr-space-one-and-a-half-x;
        column-gap: $cdr-space-one-and-a-half-x;
      }

      @include cdr-lg-mq-up {
        row-gap: $cdr-space-two-x;
        column-gap: $cdr-space-two-x;
      }

      .simple-top-categories__category {
        :deep(.simple-top-categories__link) {
          @include cdr-text-heading-sans-400;
          text-align: left;

          &:hover{
            text-decoration: none;
            .simple-top-categories__link-title__subheader{
              text-decoration: underline;
            }
          }

          .simple-top-categories__link-title {
            margin: 0;

            &__header {
              @include cdr-text-heading-sans-400;
              margin-bottom: $cdr-space-quarter-x;
              @include cdr-sm-mq-up {
                @include cdr-text-heading-sans-500;
                margin-bottom: $cdr-space-half-x;
              }

              &--sale {
                color: $cdr-color-text-sale;
              }
            }

            &__subheader {
              @include cdr-text-heading-sans-200;
              @include cdr-sm-mq-up {
                @include cdr-text-heading-sans-300;
              }
            }
          }
        }
      }
      .simple-top-categories__media-container {
        position: relative;
        height: auto;
        width: 100%;
        max-width: 100%;

        img {
          transition: all 0.35s ease-in;
          border-radius: 4px;
        }
      }
    }

    &--center {
      :deep(.simple-top-categories__grid) {
        .simple-top-categories__category {
          .simple-top-categories__link {
            text-align: center;
          }
        }
      }
    }
  }
}

</style>
